/**
 * 작명 모드별 세부 정보 입력 필드 구성 (16개 모드 공용).
 * key 형식: `${type}-${target}-${detail}` (modeKey).
 *
 * 사용처:
 *   - NamingServiceSelect / PopNamingFieldInput: 입력 폼 렌더
 *   - 추천기준보기(PopRecommendCriteria): 사용자가 입력한 값을 "작명 기준"으로 표시
 *
 * field 타입:
 *   - { type: 'text', key, label, required, placeholder, showAsterisk?, charset?, maxLength? }
 *   - { type: 'buttons', key, label, required, options:[{key,label}], showAsterisk? }
 *   - { type: 'check-buttons', key, label, required, options:[{key,label}], showAsterisk? }
 *       — buttons + 활성 시 체크 아이콘 (큰 버튼, 추천 개수용)
 *   - { type: 'radio-text', label, required, radioKey, textKey, options, placeholder?, showAsterisk?, charset?, maxLength? }
 *       — 라디오 그룹 + 텍스트 인풋 복합 (돌림자용, charset/maxLength는 textKey 인풋에 적용)
 * showAsterisk 미지정 시 required 따라감. 디자인상 asterisk만 숨기고 싶을 때 false로.
 * charset: 'korean'(완성형 한글) | 'korean-multi'(한글 + 쉼표·공백 구분 복수) | 'hanja'(한자).
 *   값이 입력된 경우에만 검사하며(선택 필드 포함), 불일치 시 제출 차단 + 안내 문구 표시.
 *   닉네임·상호·업종 등 영문/숫자가 섞일 수 있는 자유 텍스트에는 지정하지 않는다.
 * maxLength: 인풋 maxlength. 미지정 시 30 (PopNamingFieldInput 기본값).
 */
export const NAMING_FIELD_CONFIGS = {
  'recommend-person-kor': [
    { type: 'text', key: 'surname', label: '성씨', required: true, placeholder: '예: 김', charset: 'korean', maxLength: 2 },
    { type: 'buttons', key: 'gender', label: '성별', required: true, options: [
      { key: 'M', label: '남자' },
      { key: 'F', label: '여자' },
      { key: 'N', label: '공용' },
    ] },
    { type: 'buttons', key: 'nameLength', label: '이름 길이', required: true, options: [
      { key: 1, label: '1글자' },
      { key: 2, label: '2글자' },
      { key: 3, label: '3글자' },
    ] },
    { type: 'radio-text', label: '돌림자(선택)', required: false,
      radioKey: 'generationPosition',
      textKey: 'generationChars',
      options: [
        { key: 'front', label: '앞자리' },
        { key: 'end', label: '끝자리' },
        { key: 'any', label: '위치 상관없음' },
      ],
      placeholder: '예: 민, 서, 준',
      charset: 'korean-multi',
      maxLength: 10,
    },
  ],
  'recommend-person-han': [
    { type: 'text', key: 'surname', label: '성씨', required: true, placeholder: '예: 김', charset: 'korean', maxLength: 2 },
    { type: 'text', key: 'firstName', label: '이름', required: true, placeholder: '예: 민지', charset: 'korean', maxLength: 5 },
  ],
  'analyze-person-kor': [
    { type: 'text', key: 'surname', label: '성씨', required: true, placeholder: '예: 김', charset: 'korean', maxLength: 2 },
    { type: 'text', key: 'firstName', label: '이름', required: true, placeholder: '예: 민지', charset: 'korean', maxLength: 5 },
  ],
  'analyze-person-han': [
    { type: 'text', key: 'surname', label: '성씨(한자)', required: true, placeholder: '예: 吳', charset: 'hanja', maxLength: 2 },
    { type: 'text', key: 'firstName', label: '이름(한자)', required: true, placeholder: '예: 燕知', charset: 'hanja', maxLength: 5 },
  ],
  'recommend-pet-animal': [
    { type: 'buttons', key: 'animalType', label: '동물 종류', required: true, options: [
      { key: 'dog', label: '강아지' },
      { key: 'cat', label: '고양이' },
      { key: 'bird', label: '새' },
      { key: 'etc', label: '기타' },
    ] },
    { type: 'text', key: 'breed', label: '세부 품종(선택)', required: false, placeholder: '예: 골든 리트리버' },
    { type: 'buttons', key: 'preference', label: '선호 느낌', required: true, options: [
      { key: 'cute', label: '귀여운' },
      { key: 'luxury', label: '고급진' },
      { key: 'friendly', label: '친근한' },
      { key: 'unique', label: '독특한' },
    ] },
  ],
  'recommend-pet-plant': [
    { type: 'buttons', key: 'plantType', label: '식물 종류', required: true, options: [
      { key: 'foliage', label: '관엽' },
      { key: 'succulent', label: '다육' },
      { key: 'flower', label: '꽃' },
      { key: 'herb', label: '허브' },
    ] },
    { type: 'buttons', key: 'preference', label: '선호 느낌', required: true, options: [
      { key: 'fresh', label: '청초한' },
      { key: 'lush', label: '풍성한' },
      { key: 'mystic', label: '신비로운' },
      { key: 'friendly', label: '친근한' },
    ] },
  ],
  'recommend-pet-object': [
    { type: 'buttons', key: 'objectType', label: '사물 유형', required: true, options: [
      { key: 'car', label: '자동차' },
      { key: 'laptop', label: '노트북' },
      { key: 'bicycle', label: '자전거' },
      { key: 'furniture', label: '가구' },
    ] },
    { type: 'text', key: 'imageDesc', label: '희망 이미지(선택)', required: false, placeholder: '예: 든든한' },
  ],
  'analyze-pet-animal': [
    { type: 'text', key: 'petName', label: '반려동물 이름', required: true, placeholder: '예: 솔비' },
    { type: 'text', key: 'animalType', label: '동물 종류(선택)', required: false, placeholder: '예: 골든 리트리버' },
  ],
  'analyze-pet-plant': [
    { type: 'text', key: 'plantName', label: '반려식물 이름', required: true, placeholder: '예: 잎새' },
    { type: 'text', key: 'plantType', label: '식물 종류(선택)', required: false, placeholder: '예: 몬스테라' },
  ],
  'analyze-pet-object': [
    { type: 'text', key: 'objectName', label: '사물 이름', required: true, placeholder: '예: 레오' },
    { type: 'buttons', key: 'objectType', label: '사물 유형', required: true, options: [
      { key: 'car', label: '자동차' },
      { key: 'laptop', label: '노트북' },
      { key: 'bicycle', label: '자전거' },
      { key: 'furniture', label: '가구' },
    ] },
  ],
  'recommend-biz-biz': [
    { type: 'text', key: 'industry', label: '업종', required: true, placeholder: '예: IT/ 소프트웨어' },
    { type: 'buttons', key: 'brandMood', label: '브랜드 느낌', required: true, options: [
      { key: 'classic', label: '정통' },
      { key: 'modern', label: '모던' },
      { key: 'friendly', label: '친근' },
      { key: 'premium', label: '프리미엄' },
    ] },
    { type: 'buttons', key: 'nameLength', label: '상호 길이', required: true, options: [
      { key: 3, label: '3글자' },
      { key: 4, label: '4글자' },
      { key: 5, label: '5글자' },
    ] },
    { type: 'text', key: 'includeWords', label: '포함하고 싶은 단어(선택)', required: false, placeholder: '예: 소프트, 테크, 스튜디오' },
  ],
  'analyze-biz-biz': [
    { type: 'text', key: 'bizName', label: '상호명', required: true, placeholder: '예: 낭만스토어' },
    { type: 'text', key: 'industry', label: '업종', required: true, placeholder: '예: IT/소프트웨어' },
  ],
  'recommend-culture-aho': [
    { type: 'text', key: 'surname', label: '성씨', required: true, placeholder: '예: 김', charset: 'korean', maxLength: 2 },
    { type: 'text', key: 'meaning', label: '희망 의미(선택)', required: false, placeholder: '예: 맑은 바람처럼, 홍익인간' },
    { type: 'buttons', key: 'charCount', label: '글자 수', required: true, options: [
      { key: 2, label: '2글자' },
      { key: 3, label: '3글자' },
      { key: 4, label: '4글자' },
    ] },
  ],
  'analyze-culture-aho': [
    { type: 'text', key: 'ahoHanja', label: '아호 (한자)', required: true, showAsterisk: false, placeholder: '예: 淸風', charset: 'hanja', maxLength: 6 },
    { type: 'text', key: 'ahoKor', label: '한글 음 (선택)', required: false, placeholder: '예: 청풍', charset: 'korean', maxLength: 6 },
  ],
  'recommend-culture-nickname': [
    { type: 'buttons', key: 'usage', label: '사용 용도', required: true, showAsterisk: false, options: [
      { key: 'sns', label: 'SNS' },
      { key: 'game', label: '게임' },
      { key: 'community', label: '커뮤니티' },
      { key: 'general', label: '일반' },
    ] },
    { type: 'buttons', key: 'charCount', label: '글자 수', required: true, showAsterisk: false, options: [
      { key: 2, label: '2글자' },
      { key: 3, label: '3글자' },
      { key: 4, label: '4글자' },
    ] },
    { type: 'text', key: 'theme', label: '희망 테마(선택)', required: false, placeholder: '예: 자유로운 바람, 조용한 산' },
  ],
  'analyze-culture-nickname': [
    { type: 'text', key: 'nickname', label: '닉네임', required: true, placeholder: '예: 은하수' },
    { type: 'buttons', key: 'usage', label: '사용 용도', required: true, options: [
      { key: 'sns', label: 'SNS' },
      { key: 'game', label: '게임' },
      { key: 'community', label: '커뮤니티' },
      { key: 'general', label: '일반' },
    ] },
  ],
};

/** 라벨에서 "(선택)" 꼬리표 제거 */
const cleanLabel = (label) => (label || '').replace(/\s*\(선택\)/, '').trim();

/**
 * 사용자가 입력한 detailInput을 추천기준보기 "작명 기준" rows로 변환한다.
 * NAMING_FIELD_CONFIGS의 필드 정의를 그대로 따라가며, 옵션은 key→label로 환산한다.
 * 값이 비어있는(선택 안 한) 필드는 건너뛴다.
 *
 * @param {string} modeKey - `${type}-${target}-${detail}`
 * @param {object} detailInput - 사용자 입력값
 * @returns {Array<{label:string, value:string}>}
 */
export const buildNamingInputRows = (modeKey, detailInput) => {
  const fields = NAMING_FIELD_CONFIGS[modeKey] || [];
  const di = detailInput || {};
  const rows = [];
  for (const f of fields) {
    if (f.type === 'radio-text') {
      const chars = di[f.textKey];
      if (chars == null || chars === '') continue;
      const posLabel = f.options?.find((o) => o.key === di[f.radioKey])?.label;
      rows.push({ label: cleanLabel(f.label), value: posLabel ? `${chars} · ${posLabel}` : `${chars}` });
      continue;
    }
    const v = di[f.key];
    if (v == null || v === '') continue;
    const value = f.options
      ? (f.options.find((o) => o.key === v)?.label ?? String(v))
      : String(v);
    rows.push({ label: cleanLabel(f.label), value });
  }
  return rows;
};
